import { ref, onBeforeUnmount, type Ref } from "vue";

export interface UseScriptOptions {
  src: string;
  async?: boolean;
  crossorigin?: boolean | "anonymous" | "use-credentials";
  referrerpolicy?: boolean | ReferrerPolicy;
}

export interface UseScriptTriggerOptions {
  trigger?: "manual" | "immediate";
}

export interface UseScriptReturn {
  /**
   * Whether the script has been loaded
   */
  loaded: Ref<boolean>;
  /**
   * Register a callback to be called when the script is loaded
   */
  onLoaded: (callback: () => void) => void;
  /**
   * Manually trigger the script load
   */
  load: () => Promise<void>;
  /**
   * Remove the script from the DOM
   */
  unload: () => void;
}

/**
 * Composable for dynamically loading external scripts
 * @param options Script loading options
 * @param triggerOptions When to load the script
 * @returns Script loading utilities
 */
export function useScript(
  options: UseScriptOptions,
  triggerOptions: UseScriptTriggerOptions = { trigger: "immediate" }
): UseScriptReturn {
  const loaded = ref(false);
  const loadedCallbacks: (() => void)[] = [];
  let scriptElement: HTMLScriptElement | null = null;

  const onLoaded = (callback: () => void) => {
    if (loaded.value) {
      callback();
    } else {
      loadedCallbacks.push(callback);
    }
  };

  const executeCallbacks = () => {
    loadedCallbacks.forEach((callback) => callback());
    loadedCallbacks.length = 0;
  };

  const load = async (): Promise<void> => {
    // If already loaded or loading, return
    if (loaded.value || scriptElement) {
      return;
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector(
      `script[src="${options.src}"]`
    );
    if (existingScript) {
      loaded.value = true;
      executeCallbacks();
      return;
    }

    return new Promise((resolve, reject) => {
      scriptElement = document.createElement("script");
      scriptElement.src = options.src;

      // Set async attribute
      if (options.async !== false) {
        scriptElement.async = true;
      }

      // Set crossorigin attribute
      if (options.crossorigin && options.crossorigin !== false) {
        scriptElement.crossOrigin =
          typeof options.crossorigin === "string"
            ? options.crossorigin
            : "anonymous";
      }

      // Set referrerpolicy attribute
      if (options.referrerpolicy && options.referrerpolicy !== false) {
        scriptElement.referrerPolicy =
          typeof options.referrerpolicy === "string"
            ? options.referrerpolicy
            : "no-referrer-when-downgrade";
      }

      scriptElement.onload = () => {
        loaded.value = true;
        executeCallbacks();
        resolve();
      };

      scriptElement.onerror = (error) => {
        console.error(`Failed to load script: ${options.src}`, error);
        reject(new Error(`Failed to load script: ${options.src}`));
      };

      document.head.appendChild(scriptElement);
    });
  };

  const unload = () => {
    if (scriptElement && scriptElement.parentNode) {
      scriptElement.parentNode.removeChild(scriptElement);
      scriptElement = null;
      loaded.value = false;
    }
  };

  // Auto-load if trigger is immediate
  if (triggerOptions.trigger === "immediate") {
    load();
  }

  // Cleanup on unmount
  onBeforeUnmount(() => {
    loadedCallbacks.length = 0;
  });

  return {
    loaded,
    onLoaded,
    load,
    unload,
  };
}
